/**
 * Client for the Latency Metrics service (Go) exposed via the gateway at
 * `/gw/latency-metrics`. The service does not publish an OpenAPI spec yet, so
 * this is a small hand-written typed client rather than generated code — swap it
 * for codegen (types_latency_metrics) once the service serves /v3/api-docs.
 *
 * Auth reuses the globally-managed Auth0 bearer (OpenAPI.TOKEN, set in
 * useToken.ts) and the selected org (getCurrentOrg). organization_id is a
 * required query param on every endpoint.
 */
import { OpenAPI } from '../services';
import { getCurrentOrg } from './getCurrentOrg';

// The gateway exposes this service under the `/app` prefix (API_PREFIX=app),
// alongside the other /app-routed services — not the default `/gw`.
const GATEWAY = '/app/latency-metrics';
const API_PREFIX = '/api/v1/latency-metrics';

// Same pattern as the widget service (REACT_APP_BASE_API_URL + '/app/widget').
const baseUrl = (): string =>
  process.env.NODE_ENV === 'development'
    ? GATEWAY // dev: handled by the existing `/app` proxy in setupProxy.js
    : `${process.env.REACT_APP_BASE_API_URL}${GATEWAY}`;

type QueryValue = string | number | boolean | undefined | null;

const buildQuery = (params: Record<string, QueryValue>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });
  return search.toString();
};

async function request<T>(
  path: string,
  params: Record<string, QueryValue>
): Promise<T> {
  const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : '';
  const query = buildQuery({ organization_id: getCurrentOrg(), ...params });
  const response = await fetch(`${baseUrl()}${API_PREFIX}${path}?${query}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'x-ai-agent-organization-context': getCurrentOrg() || '',
    },
  });
  if (!response.ok) {
    let detail = '';
    try {
      detail = JSON.stringify(await response.json());
    } catch {
      /* ignore body parse errors */
    }
    throw new Error(
      `latency-metrics ${path} failed: ${response.status} ${detail}`
    );
  }
  return (await response.json()) as T;
}

// --- response types (mirror the Go dto.responses) ---

export type TimeseriesPoint = { timestamp: string; value: number | null };
export type TimeseriesResponse = {
  organization_id: string;
  agent_id?: string;
  metric_type?: string;
  metric: string;
  aggregation: string;
  interval: string;
  points: TimeseriesPoint[];
};

export type PercentileItem = {
  group?: string;
  count: number;
  p50: number | null;
  p95: number | null;
  p99: number | null;
};
export type PercentilesResponse = {
  metric: string;
  group_by?: string;
  items: PercentileItem[];
};

export type ModelItem = {
  provider: string;
  model: string;
  metric_type: string;
  events_count: number;
  p50_latency_ms: number | null;
  p95_latency_ms: number | null;
  p99_latency_ms: number | null;
};
export type ModelsResponse = { items: ModelItem[] };

export type FallbackItem = {
  component?: string;
  failed_provider?: string;
  failed_model?: string;
  fallback_provider?: string;
  fallback_model?: string;
  chain_exhausted?: boolean;
  count: number;
};
export type FallbacksResponse = { items: FallbackItem[] };

export type ToolCallItem = {
  timestamp: string;
  conversation_id?: string;
  speech_id?: string;
  tool_name: string;
  tool_type?: string;
  execution_time_ms: number | null;
  success: boolean | null;
  error_type: string | null;
  error_message: string | null;
  arguments?: string;
};
export type ToolCallsResponse = { items: ToolCallItem[] };

export type ThroughputItem = {
  metric_type: string;
  total_events: number;
  avg_rps: number;
  peak_rps: number;
};
export type ThroughputResponse = {
  from: string;
  to: string;
  window_seconds: number;
  items: ThroughputItem[];
};

export type ThroughputSeriesPoint = {
  timestamp: string;
  count: number;
  rps: number;
};
export type ThroughputSeries = {
  model: string;
  points: ThroughputSeriesPoint[];
};
export type ThroughputSeriesResponse = {
  interval: string;
  metric_type?: string;
  series: ThroughputSeries[];
};

// --- request params ---

type RangeFilters = {
  from: string;
  to: string;
  agentId?: string;
  conversationId?: string;
};

// --- endpoint wrappers ---

export const getLatencyTimeseries = (
  params: RangeFilters & {
    metricType: string;
    metric: string;
    aggregation: string;
    interval: string;
  }
): Promise<TimeseriesResponse> =>
  request<TimeseriesResponse>('/timeseries', {
    from: params.from,
    to: params.to,
    metric_type: params.metricType,
    metric: params.metric,
    aggregation: params.aggregation,
    interval: params.interval,
    agent_id: params.agentId,
    conversation_id: params.conversationId,
  });

export const getLatencyPercentiles = (
  params: RangeFilters & {
    metricType: string;
    metric: string;
    groupBy?: string;
  }
): Promise<PercentilesResponse> =>
  request<PercentilesResponse>('/percentiles', {
    from: params.from,
    to: params.to,
    metric_type: params.metricType,
    metric: params.metric,
    group_by: params.groupBy,
    agent_id: params.agentId,
    conversation_id: params.conversationId,
  });

export const getLatencyModels = (
  params: RangeFilters
): Promise<ModelsResponse> =>
  request<ModelsResponse>('/models', {
    from: params.from,
    to: params.to,
    agent_id: params.agentId,
    conversation_id: params.conversationId,
  });

export const getLatencyFallbacks = (
  params: RangeFilters & { groupBy?: string }
): Promise<FallbacksResponse> =>
  request<FallbacksResponse>('/fallbacks', {
    from: params.from,
    to: params.to,
    group_by: params.groupBy,
    agent_id: params.agentId,
    conversation_id: params.conversationId,
  });

export const getLatencyToolCalls = (
  params: RangeFilters & { limit?: number }
): Promise<ToolCallsResponse> =>
  request<ToolCallsResponse>('/tool-calls', {
    from: params.from,
    to: params.to,
    limit: params.limit ?? 100,
    agent_id: params.agentId,
    conversation_id: params.conversationId,
  });

export const getLatencyThroughput = (
  params: RangeFilters & { metricType?: string; model?: string }
): Promise<ThroughputResponse> =>
  request<ThroughputResponse>('/throughput', {
    from: params.from,
    to: params.to,
    metric_type: params.metricType,
    model: params.model,
    agent_id: params.agentId,
    conversation_id: params.conversationId,
  });

export const getLatencyThroughputSeries = (
  params: RangeFilters & { metricType?: string; interval: string }
): Promise<ThroughputSeriesResponse> =>
  request<ThroughputSeriesResponse>('/throughput/timeseries', {
    from: params.from,
    to: params.to,
    metric_type: params.metricType,
    interval: params.interval,
    agent_id: params.agentId,
    conversation_id: params.conversationId,
  });
